import { useHandler } from "@redotech/react-util/hook";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { ToolComponentProps } from "@redotech/redo-web/ai/chat/types/tool-component-props";
import { buildIntercomTranscript } from "@redotech/redo-web/ai/chat/utils/messages";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useContext } from "react";
import { useIntercom } from "react-use-intercom";
import { useInChatEscalationFlag } from "./in-chat-escalation-flag-provider";
import { useInChatEscalation } from "./use-in-chat-escalation";

const buildIntercomMessage = (chatHistory: string) => {
  const currentUrl = window.location.href;
  return `We copied your conversation up to this point. Please press send to continue!\n\n---\n\n${chatHistory}\n\nLink: ${currentUrl}`;
};

const closeChatInterface = () => {
  const chatCloseButton = document.querySelector(
    '[data-testid="chat-close-button"]',
  );
  if (chatCloseButton instanceof HTMLElement) {
    chatCloseButton.click();
  }
};

export function EscalateToHumanTool(props: ToolComponentProps) {
  const inChatEscalation = useInChatEscalationFlag();
  const { sendToolResponse, messages } = useContext(AiChatContext);
  const intercom = useIntercom();

  const sendEscalationResponse = useHandler(() => {
    void sendToolResponse(
      {
        toolCallId: props.toolCallId,
        toolName: props.toolName,
        result: { output: "Conversation escalated to human support" },
      },
      { skipLlmCall: true },
    );
  });

  // Legacy behavior: Open Intercom popup
  const handleTalkToHuman = useHandler(() => {
    const chatHistory = buildIntercomTranscript(messages as any);
    const intercomMessage = buildIntercomMessage(chatHistory);

    try {
      intercom.showNewMessage(intercomMessage);
      sendEscalationResponse();
      closeChatInterface();
    } catch {
      sendEscalationResponse();
    }
  });

  // New behavior: In-chat escalation
  const { escalate, isLoading, error, isEscalated } = useInChatEscalation({
    messages,
    input: props.input,
    onSuccess: sendEscalationResponse,
  });

  const handleEscalation = inChatEscalation ? escalate : handleTalkToHuman;

  if (inChatEscalation && isEscalated) {
    return null;
  }

  return (
    <Flex
      align="center"
      bgColor="primary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      m="lg"
      p="lg"
      radius="md"
      shadow="lg"
    >
      <Flex align="center" dir="column" gap="sm">
        <div
          style={{
            padding: "12px",
            borderRadius: "50%",
            backgroundColor: "var(--redo-colors-background-bg-brand-subtle)",
            border: "1px solid var(--redo-colors-border-border-brand)",
          }}
        >
          <RedoIcon color="brand" Icon={MessageChatCircleIcon} size={20} />
        </div>

        <Text fontSize="md" fontWeight="semibold" textAlign="center">
          Need human assistance?
        </Text>

        <Text color="secondary" fontSize="sm" textAlign="center">
          Our support team is here to help. We'll transfer your conversation and
          connect you with a specialist.
        </Text>
      </Flex>

      {error && (
        <Text color="error" fontSize="xs" textAlign="center">
          {error}
        </Text>
      )}

      <RedoButton
        disabled={isLoading || props.disabled}
        hierarchy="primary"
        onClick={handleEscalation}
        pending={isLoading}
        size="sm"
        style={{ minWidth: "140px" }}
        text={isLoading ? "Connecting..." : "Talk to Support"}
      />
    </Flex>
  );
}
